import type { SectionContent } from '../../types.js';

export const gstackContent: SectionContent = {
  id: 'gstack',
  label: 'gstack',
  repoUrl: 'https://github.com/garrytan/gstack',
  intro:
    'gstack 是由 Y Combinator 总裁兼 CEO Garry Tan 开源的工具包，把 Claude Code 变成一支虚拟工程团队：CEO、设计师、工程经理、发布工程师、QA 负责人、安全官、iOS 测试员等角色，全部通过 slash 命令调用。项目宣传语称其为"23 个专职角色和 8 个强力工具"，但仓库实际收录的技能已随版本迭代增长到 45 个。它想解决的是 AI 辅助开发中常见的问题：缺乏角色结构、规划/构建/评审/测试/发布各环节工作流碎片化、AI 生成代码缺少人工把关，以及跨会话的知识丢失。其核心理念是"配备合适工具的单人开发者能比传统团队跑得更快"。',
  install: [
    {
      name: 'git clone gstack',
      description: '克隆仓库到 ~/.claude/skills 并运行安装脚本，一次性完成安装。',
      example:
        'git clone --single-branch --depth 1 https://github.com/garrytan/gstack.git ~/.claude/skills/gstack && cd ~/.claude/skills/gstack && ./setup',
    },
    {
      name: './setup --host',
      description: '为 Claude Code 之外的其他 AI 编程平台安装 gstack（支持 cursor、opencode、codex 等）。',
      example: './setup --host {host}',
      slots: { host: 'cursor' },
    },
  ],
  skills: [
    { name: 'office-hours', description: '通过一系列逼问式问题，对产品想法进行深度质询。', example: '/office-hours' },
    { name: 'plan-ceo-review', description: '以 CEO 视角对计划的战略范围提出挑战（支持 4 种模式）。', example: '/plan-ceo-review' },
    { name: 'plan-eng-review', description: '对计划的架构设计、数据流和边界情况进行工程评审。', example: '/plan-eng-review' },
    { name: 'plan-design-review', description: '按设计维度对计划进行 0-10 分的评审打分。', example: '/plan-design-review' },
    { name: 'plan-devex-review', description: '以交互方式评审计划的开发者体验（DevEx）。', example: '/plan-devex-review' },
    { name: 'devex-review', description: '实测开发者上手体验：走查文档、体验新手引导流程并截图报错，与 plan-devex-review 的评分做对比。', example: '/devex-review' },
    { name: 'autoplan', description: '自动依次运行 CEO、设计、工程三项评审。', example: '/autoplan' },
    { name: 'design-consultation', description: '从零开始生成一套完整的设计系统。', example: '/design-consultation' },
    { name: 'design-shotgun', description: '一次性生成 4-6 个 AI 视觉稿变体供挑选。', example: '/design-shotgun' },
    { name: 'design-html', description: '将视觉稿转换为可用于生产环境的 HTML/CSS。', example: '/design-html' },
    { name: 'design-review', description: '对实际界面进行实时设计审查并自动修复问题。', example: '/design-review' },
    { name: 'diagram', description: '把自然语言描述转换成可编辑的图表（mermaid/excalidraw）。', example: '/diagram' },
    { name: 'make-pdf', description: '将 Markdown 转换为可发布质量的 PDF/DOCX/HTML 文档。', example: '/make-pdf' },
    { name: 'review', description: '以资深工程师视角对代码进行审查。', example: '/review' },
    { name: 'codex', description: '向 OpenAI Codex CLI 请求第二意见。', example: '/codex' },
    { name: 'investigate', description: '对问题进行系统化的根因排查。', example: '/investigate' },
    { name: 'spec', description: '把模糊的意图转化为可执行的规格说明。', example: '/spec' },
    { name: 'qa', description: '打开真实浏览器查找并修复 bug，支持自动化测试。', example: '/qa {url}', slots: { url: 'https://staging.myapp.com' } },
    { name: 'qa-only', description: '仅报告 bug，不对代码做任何修改。', example: '/qa-only' },
    { name: 'benchmark', description: '基准测量页面加载时间、Core Web Vitals 和资源体积，为每次 PR 提供前后对比。', example: '/benchmark' },
    { name: 'canary', description: '在部署后进行持续监控的巡检循环。', example: '/canary' },
    { name: 'ship', description: '同步主干、运行测试、审计覆盖率并开出 PR。', example: '/ship' },
    { name: 'land-and-deploy', description: '合并 PR 并验证生产环境健康状况。', example: '/land-and-deploy' },
    { name: 'document-release', description: '自动更新所有文档以匹配已发布的代码。', example: '/document-release' },
    { name: 'document-generate', description: '按 Diataxis 框架生成缺失的文档。', example: '/document-generate' },
    { name: 'browse', description: '用真实 Chromium 浏览器进行截图与点击操作（约 100ms 级响应）。', example: '/browse' },
    { name: 'open-gstack-browser', description: '启动带有侧边栏智能体的 GStack 浏览器。', example: '/open-gstack-browser' },
    { name: 'pair-agent', description: '通过共享浏览器实现多个 AI 智能体协同工作。', example: '/pair-agent' },
    { name: 'setup-browser-cookies', description: '从真实 Chrome 会话导入 Cookie。', example: '/setup-browser-cookies' },
    { name: 'learn', description: '管理跨会话积累的经验与模式。', example: '/learn' },
    { name: 'retro', description: '生成每周工程复盘报告。', example: '/retro' },
    { name: 'cso', description: '按 OWASP Top 10 与 STRIDE 模型进行安全审计。', example: '/cso' },
    { name: 'setup-deploy', description: '一次性配置部署平台相关设置。', example: '/setup-deploy' },
    { name: 'setup-gbrain', description: '从零搭建持续可用的知识库（GBrain）。', example: '/setup-gbrain' },
    { name: 'sync-gbrain', description: '将代码库重新索引进知识库。', example: '/sync-gbrain' },
    { name: 'gstack-upgrade', description: '将 gstack 自身更新到最新版本。', example: '/gstack-upgrade' },
    { name: 'careful', description: '在执行 rm、DROP、force-push 等破坏性命令前发出警告。', example: '/careful' },
    { name: 'freeze', description: '将编辑范围锁定在单个目录内。', example: '/freeze' },
    { name: 'guard', description: '同时启用 careful 和 freeze 两种防护。', example: '/guard' },
    { name: 'unfreeze', description: '解除 freeze 设置的目录编辑限制。', example: '/unfreeze' },
    { name: 'ios-qa', description: '通过 USB 使用 CoreDevice 驱动真机 iPhone 进行测试。', example: '/ios-qa' },
    { name: 'ios-fix', description: '在真机 iOS 设备上执行 bug 修复循环。', example: '/ios-fix' },
    { name: 'ios-design-review', description: '按 Apple HIG 规范对 iOS 界面进行设计审查。', example: '/ios-design-review' },
    { name: 'ios-clean', description: '清理 iOS 调试桥的相关状态。', example: '/ios-clean' },
    { name: 'ios-sync', description: '重新同步 iOS 访问器（accessor）。', example: '/ios-sync' },
  ],
  usage: [
    { name: 'gstack-model-benchmark', description: '独立 CLI，用于跨模型（Claude/GPT/Gemini）性能对比。', example: 'gstack-model-benchmark' },
    { name: 'gstack-taste-update', description: '独立 CLI，用于学习并更新设计偏好。', example: 'gstack-taste-update' },
    { name: 'gstack-ios-qa-daemon', description: '独立 CLI，作为 Mac 端与 iPhone 通信的代理进程。', example: 'gstack-ios-qa-daemon' },
    { name: 'gstack-ios-qa-mint', description: '独立 CLI，用于管理 iOS 设备白名单。', example: 'gstack-ios-qa-mint' },
    { name: 'gstack-analytics', description: '独立 CLI，展示个人使用情况仪表盘。', example: 'gstack-analytics' },
    { name: 'gstack-config', description: '独立 CLI，用于读写 gstack 的配置项。', example: 'gstack-config set {key} {value}', slots: { key: 'checkpoint_mode', value: 'continuous' } },
  ],
};
